import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { LoadingState } from '@usf/ngrx-product';
import { Product } from '@shared/models/product.model';
import { Observable } from 'rxjs';
import { PlatformEnum } from '@panamax/app-state';
import { FilterOption } from '@shared/components/filter-modal/filter-modal.component';
import { AIListBuilderService } from '../../services/ai-list-builder.service';

export interface IngredientMenuItem {
  id: string;
  name: string;
  imageUrl?: string;
}

@Component({
  selector: 'app-ai-list-ingredient-menu',
  templateUrl: './ai-list-ingredient-menu.component.html',
  styleUrls: ['./ai-list-ingredient-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiListIngredientMenuComponent {
  @Input() title = '';
  @Input() searchPlaceholder = 'Search For Product Name';
  @Input() platformType: PlatformEnum;
  @Input() filterOptions: FilterOption[] = [
    { id: 'direct', label: 'i18n.lists.direct', selected: false },
    {
      id: 'early-cutoff',
      label: 'i18n.lists.earlyCutoff',
      selected: false,
    },
    {
      id: 'discontinued',
      label: 'i18n.lists.discontinuedWhenOut',
      selected: false,
    },
    {
      id: 'exclusive',
      label: 'i18n.lists.exclusiveBrands',
      selected: false,
    },
  ];

  @Input() ingredientsWithSelectionInfo$: Observable<{
    ingredients: Product[];
    matchingProductNumber: number | null;
  }>;
  @Input() loadingState$: Observable<LoadingState>;
  @Input() loaded$: Observable<boolean>;
  @Input() loading$: Observable<boolean>;
  @Input() selectedProduct$: Observable<Product | null>;

  @Output() cancel: EventEmitter<void> = new EventEmitter();
  @Output() save: EventEmitter<Product> = new EventEmitter();
  @Output() filter: EventEmitter<void> = new EventEmitter();
  @Output() search: EventEmitter<string> = new EventEmitter();

  platformRef = PlatformEnum;
  searchQuery = '';
  previousSearchQuery = '';
  showFilterView = false;
  selectedFilterCount = 0;

  private hasFiltersBeenSavedBefore = false;

  constructor(private aiListBuilderService: AIListBuilderService) {}

  onCancel(): void {
    this.resetAllState();

    this.aiListBuilderService.clearSelectedProduct();
    this.cancel.emit();
  }

  onFilter(): void {
    this.showFilterView = true;
    this.filter.emit();
  }

  toggleFilterOption(option: FilterOption) {
    option.selected = !option.selected;
  }

  applyFilterOptions(): void {
    const menuFilters: string[] = [];

    for (const option of this.filterOptions) {
      if (option.selected) {
        menuFilters.push(option.id);
      }
    }

    this.aiListBuilderService.updateMenuFilterOptions(menuFilters);

    this.searchInIngredientMenu(this.searchQuery, true);
  }

  onSave(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.showFilterView) {
      this.saveFilterOptions();
    } else {
      this.saveProductSelection();
    }
  }

  shouldEnableSaveInFilterView(): boolean {
    if (this.filterOptions.some(option => option.selected)) {
      return true;
    }

    // If no options are selected but filters have been saved before,
    // we should still enable the save button to allow clearing all filters
    return this.hasFiltersBeenSavedBefore && this.selectedFilterCount > 0;
  }

  private saveFilterOptions(): void {
    this.updateSelectedFilterCount();

    this.hasFiltersBeenSavedBefore = true;

    this.applyFilterOptions();

    this.showFilterView = false;
  }

  saveProductSelection(): void {
    const selectedProduct = this.aiListBuilderService.getSelectedProduct();
    if (selectedProduct) {
      this.save.emit(selectedProduct);
    }
  }

  onSearch(event: any): void {
    const query = event.target.value || '';
    this.searchQuery = query;

    this.searchInIngredientMenu(this.searchQuery);

    this.search.emit(this.searchQuery);
  }

  searchInIngredientMenu(
    searchKey: string,
    forceSearch: boolean = false,
  ): void {
    if (forceSearch || searchKey !== this.previousSearchQuery) {
      this.aiListBuilderService.searchInIngredientMenu(searchKey);
      this.previousSearchQuery = searchKey;
    }
  }

  onProductSelected(product: Product): void {
    this.aiListBuilderService.setSelectedProduct(product);
  }

  isProductSelected(product: Product): boolean {
    const selectedProduct = this.aiListBuilderService.getSelectedProduct();
    return selectedProduct?.productNumber === product.productNumber;
  }

  isMatchingProduct(
    product: Product,
    matchingProductNumber: number | null,
  ): boolean {
    return (
      matchingProductNumber !== null &&
      product.productNumber === matchingProductNumber
    );
  }

  hasFilterChanges(): boolean {
    if (!this.filterOptions) {
      return false;
    }

    return this.filterOptions.some(option => option.selected);
  }

  clearAllFilters() {
    this.filterOptions.forEach(option => (option.selected = false));
  }

  private resetAllState(): void {
    this.aiListBuilderService.clearSelectedProduct();
    this.aiListBuilderService.clearProductSearchRestults();

    this.showFilterView = false;
    this.filterOptions.forEach(option => (option.selected = false));

    // Reset search and filter count
    this.searchQuery = '';
    this.previousSearchQuery = '';
    this.selectedFilterCount = 0;
  }

  private updateSelectedFilterCount() {
    this.selectedFilterCount = this.filterOptions.filter(
      o => o.selected,
    ).length;
  }

  resetFilterView(): void {
    this.showFilterView = false;
  }
}
